<template>
  <f7-page name="admin-reward-rules">
    <f7-navbar :title="$t('growth.rewardRules.title')" back-link="Back" />
     <AppLayout>
  <template #main>
    <f7-toolbar bottom>
      <f7-link  @click="openCreateRule">
        <f7-icon f7="plus" slot="icon" class="t-green"></f7-icon>
        <span>{{ $t('growth.rewardRules.addRule') }}</span>
      </f7-link>
    </f7-toolbar>

    <f7-block class="pb-0 growth-page no-padding no-margin">
      <!-- Campaign Filter -->
      <f7-card class="growth-card mb-3">
        <f7-card-content>
          <f7-list>
            <f7-list-item
              :title="$t('growth.rewardRules.campaign')"
              smart-select
              :smart-select-params="{ openIn:isSmallScreen? 'sheet':'popup', closeOnSelect: true, sheetSwipeToClose: true }"
            >
              <select name="campaignFilter" :value="selectedCampaignId" @change="onCampaignChange">
                <option value="">{{ $t('growth.rewardRules.allCampaigns') }}</option>
                <option
                  v-for="c in campaigns"
                  :key="c.id"
                  :value="c.id"
                >{{ c.title }}</option>
              </select>
            </f7-list-item>
          </f7-list>
        </f7-card-content>
      </f7-card>

      <!-- Reward Rules List -->
      <f7-card class="growth-card">
        <f7-card-header>
          <f7-icon f7="gift" size="22" class="t-gold" />
          <div class="t-bold">{{ $t('growth.rewardRules.activeRules') }}</div>
        </f7-card-header>
        <f7-card-content>
          <f7-list v-if="rules.length > 0" class="growth-list">
            <f7-list-item
              v-for="rule in rules"
              :key="rule.id"
              :title="formatTrigger(rule.trigger)"
              :subtitle="$t('growth.rewardRules.rewardSummary', { amount: rule.rewardAmount, type: formatRewardType(rule.rewardType) })"
              :footer="$t('growth.rewardRules.ruleDetail', { beneficiary: formatBeneficiary(rule.beneficiary), release: formatReleaseType(rule.releaseType) })"
              swipeout
              @click="editRule(rule)"
            >
              <template #after>
                <f7-toggle
                  :checked="rule.enabled"
                  @change="toggleRule(rule, $event.target.checked)"
                ></f7-toggle>
              </template>

              <template #swipeout>
                <f7-swipeout-actions right>
                  <f7-swipeout-button color="red" @click="deleteRule(rule)">{{ $t('growth.rewardRules.delete') }}</f7-swipeout-button>
                </f7-swipeout-actions>
              </template>
            </f7-list-item>
          </f7-list>

          <div v-else class="text-align-center py-4 muted">
            {{ $t('growth.rewardRules.noRules') }}
          </div>
        </f7-card-content>
      </f7-card>

      <!-- Statistics Card -->
      <f7-card class="growth-card mt-3">
        <f7-card-content>
          <div class="stats-row">
            <div class="stat text-align-center">
              <div class="t-h4 t-blue t-bold">{{ rules.length }}</div>
              <div class=" t-small">{{ $t('growth.rewardRules.totalRules') }}</div>
            </div>
            <div class="stat text-align-center">
              <div class="t-h4 t-green t-bold">{{ enabledCount }}</div>
              <div class=" t-small">{{ $t('growth.rewardRules.enabled') }}</div>
            </div>
            <div class="stat text-align-center">
              <div class="t-h4 t-terracotta t-bold">{{ disabledCount }}</div>
              <div class="t-muted t-small">{{ $t('growth.rewardRules.disabled') }}</div>
            </div>
          </div>
        </f7-card-content>
      </f7-card>
    </f7-block>
       </template>
</AppLayout>
    <!-- Create/Edit Rule Modal -->
    <AdaptiveSheet class="rule-modal" :close-by-backdrop-click="true" :close-on-escape="true" :swipe-to-close="true">
      <f7-page>
        <f7-navbar :title="$t('growth.rewardRules.ruleFormTitle')">
         <f7-nav-right> <f7-link right sheet-close popup-close><f7-icon ios="f7:multiply" md="material:close" /></f7-link></f7-nav-right>
        </f7-navbar>
        <f7-block >
          <f7-list form strong outline inset>
            <f7-list-item
              :title="$t('growth.rewardRules.campaign')"
              smart-select
              :smart-select-params="{ openIn: isSmallScreen? 'sheet':'popup', closeOnSelect: true, sheetSwipeToClose: true }"
            >
              <select name="ruleCampaign" v-model="formData.campaignId" required>
                <option
                  v-for="c in campaigns"
                  :key="c.id"
                  :value="c.id"
                >{{ c.title }}</option>
              </select>
            </f7-list-item>

            <f7-list-item
              :title="$t('growth.rewardRules.triggerEvent')"
              smart-select
              :smart-select-params="{ openIn: isSmallScreen? 'sheet':'popup', closeOnSelect: true, sheetSwipeToClose: true }"
            >
              <select name="ruleTrigger" v-model="formData.trigger" required>
                <option
                  v-for="t in triggerOptions"
                  :key="t"
                  :value="t"
                >{{ formatTrigger(t) }}</option>
              </select>
            </f7-list-item>

            <f7-list-input
              v-model="formData.rewardAmount"
              type="number"
              :label="$t('growth.rewardRules.rewardAmount')"
              :placeholder="$t('growth.rewardRules.exampleAmount')"
              required
            ></f7-list-input>

            <f7-list-item
              :title="$t('growth.rewardRules.rewardType')"
              smart-select
              :smart-select-params="{ openIn:isSmallScreen? 'sheet':'popup', closeOnSelect: true, sheetSwipeToClose: true }"
            >
              <select name="ruleRewardType" v-model="formData.rewardType" required>
                <option
                  v-for="t in rewardTypeOptions"
                  :key="t"
                  :value="t"
                >{{ formatRewardType(t) }}</option>
              </select>
            </f7-list-item>

            <f7-list-item
              :title="$t('growth.rewardRules.releaseType')"
              smart-select
              :smart-select-params="{ openIn:isSmallScreen? 'sheet':'popup', closeOnSelect: true, sheetSwipeToClose: true }"
            >
              <select name="ruleReleaseType" v-model="formData.releaseType" required>
                <option
                  v-for="t in releaseTypeOptions"
                  :key="t"
                  :value="t"
                >{{ formatReleaseType(t) }}</option>
              </select>
            </f7-list-item>

            <f7-list-item
              :title="$t('growth.rewardRules.beneficiary')"
              smart-select
              :smart-select-params="{ openIn: isSmallScreen? 'sheet':'popup', closeOnSelect: true, sheetSwipeToClose: true }"
            >
              <select name="ruleBeneficiary" v-model="formData.beneficiary" required>
                <option value="COMMUNITY">{{ $t('growth.rewardRules.community') }}</option>
                <option value="MEMBER">{{ $t('growth.rewardRules.member') }}</option>
                <option value="COMMUNITY_WALLET">{{ $t('growth.rewardRules.communityWallet') }}</option>
              </select>
            </f7-list-item>

            <f7-list-input
              v-model="formData.maxOccurrences"
              type="number"
              :label="$t('growth.rewardRules.maxOccurrences')"
              :placeholder="$t('growth.rewardRules.optionalLimit')"
            ></f7-list-input>

            <f7-list-item
              :title="$t('growth.rewardRules.enabled')"
            >
              <template #after>
                <f7-toggle v-model="formData.enabled"></f7-toggle>
              </template>
            </f7-list-item>
          </f7-list>

          <f7-block class="mt-4">
            <f7-button large fill round raised class="btn-green" @click="saveRule" block>{{ $t('growth.rewardRules.saveRule') }}</f7-button>
          </f7-block>
        </f7-block>
      </f7-page>
    </AdaptiveSheet>
  </f7-page>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import {
  f7Page, f7Navbar, f7Toolbar, f7Link, f7Icon, f7Block, f7Card, f7CardHeader,
  f7CardContent, f7List, f7ListItem, f7ListInput, f7Toggle, f7Button,
  f7Popup, f7SwipeoutActions, f7SwipeoutButton
} from 'framework7-vue'
import { f7 } from 'framework7-vue'
import { useGrowthStore } from '../js/pinia-stores/growth.js'
import AppLayout from '../components/common/AppLayout.vue'
import AdaptiveSheet from '../components/common/AdaptiveSheet.vue'
import { openAdaptiveModal, closeAdaptiveModal } from '../js/composables/useAdaptiveModal.js'
import { useSmallScreen } from '../js/composables/useMediaQuery.js'


import i18n from '../js/i18n.js'
const { t } = i18n.global

const props = defineProps({ campaignId: { type: String, default: '' } })

const growthStore = useGrowthStore()
const isSmallScreen = useSmallScreen().matches
const rules = computed(() => growthStore.rewardRules)
const campaigns = computed(() => growthStore.campaigns)
const selectedCampaignId = ref(props.campaignId || '')
const editingRule = ref(null)

const formData = ref({
  campaignId: '',
  trigger: '',
  rewardAmount: 0,
  rewardType: '',
  releaseType: '',
  beneficiary: 'COMMUNITY',
  maxOccurrences: null,
  enabled: true,
})

const enabledCount = computed(() => rules.value.filter((r) => r.enabled).length)
const disabledCount = computed(() => rules.value.filter((r) => !r.enabled).length)

const triggerOptions = [
  'MEMBER_JOINED', 'COMMUNITY_VERIFIED', 'CAMPAIGN_COMPLETED',
  'CAMPAIGN_TARGET_REACHED', 'FIRST_20_MEMBERS', 'FIRST_50_MEMBERS',
  'FIRST_100_MEMBERS', 'TRUST_LEVEL_GOLD', 'TRUST_LEVEL_PLATINUM',
]

const rewardTypeOptions = [
  'GROWTH_CREDIT', 'TRUST_POINT', 'CASH', 'BADGE',
  'MARKETPLACE_CREDIT', 'PREMIUM_ACCESS', 'BOOST', 'VOUCHER',
]

const releaseTypeOptions = [
  'IMMEDIATE', 'STAGED', 'DEFERRED', 'MANUAL',
  'CONDITIONAL', 'PERFORMANCE', 'NEVER_AUTO',
]

onMounted(async () => {
  await loadCampaigns()
  await loadRules()
})

async function loadCampaigns() {
  try {
    await growthStore.fetchRewardCampaigns()
  } catch (e) {
    f7.dialog.alert(t('growth.rewardRules.errorLoadingCampaigns', { message: e.message }))
  }
}

async function loadRules() {
  try {
    const params = selectedCampaignId.value ? { campaignId: selectedCampaignId.value } : {}
    await growthStore.fetchRewardRules(params)
  } catch (e) {
    f7.dialog.alert(t('growth.rewardRules.errorLoadingRules', { message: e.message }))
  }
}

function onCampaignChange(e) {
  selectedCampaignId.value = e.target.value
  loadRules()
}

function openCreateRule() {
  editingRule.value = null
  formData.value = {
    campaignId: campaigns.value[0]?.id || '',
    trigger: '',
    rewardAmount: 0,
    rewardType: 'GROWTH_CREDIT',
    releaseType: 'IMMEDIATE',
    beneficiary: 'COMMUNITY',
    maxOccurrences: null,
    enabled: true,
  }
  openAdaptiveModal('.rule-modal')
}

function editRule(rule) {
  editingRule.value = rule
  formData.value = { ...rule }
  openAdaptiveModal('.rule-modal')
}

async function saveRule() {
  if (!formData.value.trigger || !formData.value.rewardAmount) {
    f7.dialog.alert(t('growth.rewardRules.fillRequired'))
    return
  }

  try {
    if (editingRule.value) {
      await growthStore.updateRewardRule(editingRule.value.id, formData.value)
      f7.toast.show({ text: t('growth.rewardRules.ruleUpdated'), position: 'bottom' })
    } else {
      await growthStore.createRewardRule(formData.value)
      f7.toast.show({ text: t('growth.rewardRules.ruleCreated'), position: 'bottom' })
    }

    closeAdaptiveModal('.rule-modal')
    await loadRules()
  } catch (error) {
    f7.dialog.alert(t('growth.rewardRules.errorSaving', { message: error.message }))
  }
}

function toggleRule(rule, enabled) {
  growthStore.toggleRewardRule(rule.id, enabled).then(() => {
    f7.toast.show({
      text: t(enabled ? 'growth.rewardRules.ruleEnabled' : 'growth.rewardRules.ruleDisabled'),
      position: 'bottom',
    })
  }).catch((e) => {
    f7.dialog.alert(t('growth.rewardRules.errorToggling', { message: e.message }))
  })
}

function deleteRule(rule) {
  f7.dialog.confirm(
    t('growth.rewardRules.confirmDelete'),
    t('growth.rewardRules.deleteRule'),
    async () => {
      try {
        await growthStore.deleteRewardRule(rule.id)
        f7.toast.show({ text: t('growth.rewardRules.ruleDeleted'), position: 'bottom' })
      } catch (error) {
        f7.dialog.alert(t('growth.rewardRules.errorDeleting', { message: error.message }))
      }
    }
  )
}

function formatTrigger(trigger) {
  return humanize(trigger)
}

function formatRewardType(type) {
  return humanize(type)
}

function formatReleaseType(type) {
  return humanize(type)
}

function formatBeneficiary(beneficiary) {
  return humanize(beneficiary)
}

function humanize(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase())
}
</script>

<style scoped>
/* All inks resolve through theme-flipping tokens — readable in both modes. */
.growth-page {
  --avo-green: var(--f7-theme-color, #1a6b42);
  --avo-gold: var(--f7-secondary, #c9a84c);
  --avo-blue: var(--f7-blue, #3b4fca);
  --avo-terracotta: var(--f7-error, #ba1a1a);
  --avo-muted: var(--f7-on-surface-variant, #6f7a71);
}

.t-bold { font-weight: 600; }
.t-h4 { font-size: 18px; font-weight: 600; }
.t-small { font-size: 12px; }
.t-muted { color: var(--avo-muted); }

.t-green { color: var(--avo-green); }
.t-gold { color: var(--avo-gold); }
.t-blue { color: var(--avo-blue); }
.t-terracotta { color: var(--avo-terracotta); }

.growth-card { border-radius: 14px; }
.growth-card :deep(.card-header) { font-size: 15px; gap: 8px; }

.stats-row { display: flex; justify-content: space-between; }
.stat { flex: 1; }

.growth-list :deep(.item-title) { font-size: 14px; }
.growth-list :deep(.item-subtitle) { font-size: 12px; }
.growth-list :deep(.item-footer) { font-size: 11px; }

.btn-green {
  --f7-button-bg-color: var(--avo-green);
  --f7-button-text-color: var(--f7-on-primary, #fff);
}
</style>
